import { array } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service, SourceInfo } from 'webapp-types';

import joinClasses from 'webapp/helpers/join-classes';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import BoardImagePreviewComponent from 'webapp/components/board/image-preview';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import SubmitButtonComponent from 'webapp/components/submit-button';
import UsedSourcesListComponent from 'webapp/components/used-sources-list';

import type Board from 'webapp/models-custom/board';

interface Args {
	model: Board;
	trackingOrigin: string; // TrackingOriginForTemplateCreated;  from 'webapp/services/model-manager/boards';
	mainActionText: string;
	mainAction: (board: Board) => void;
	isVisibleOnScreen?: boolean;
	onPreview?: (board: Board) => void;
}

export default class BoardTemplatesGridItemComponent extends Component<Args> {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	@tracked isVisibleOnScreen = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.args.isVisibleOnScreen) {
			this.isVisibleOnScreen = true; // used on boards home page to always show grid item
		}
	}

	get sourcesUsed(): SourceInfo[] {
		const sourcesUsed: SourceInfo[] = [],
			sourceTypesOnBoard = this.args.model.sourcesObjs.map((source) => source.sourceType.id);

		this.mmSourceTypesService.sourceTypeModels.forEach((sourceType) => {
			if (sourceType.id && sourceTypesOnBoard.includes(sourceType.id)) {
				sourcesUsed.push({
					value: sourceType.id,
					label: sourceType.name,
					avatar: sourceType.avatar,
				});
			}
		});

		return sourcesUsed;
	}

	@action
	openPreview() {
		this.args.onPreview?.(this.args.model);
	}

	@action
	mainAction() {
		this.args.mainAction(this.args.model);
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}
	<template>
		<li
			data-id={{@model.id}}
			class={{joinClasses
				(array "card" "card--preview" "card--board" "flex-item" "filter-sort-item")
			}}
		>
			<BoardImagePreviewComponent
				@image={{@model.image}}
				@color={{@model.color}}
				@theme={{@model.bgTheme}}
				@isVisibleOnScreen={{this.isVisibleOnScreen}}
				@isLazyLoaded={{true}}
			/>

			{{#if this.isVisibleOnScreen}}

				<div class="overlay">

					<div class="overlay-bg"></div>

					<div class="wrap">
						<div class="actions">
							<div class="actions__top">
								{{#if @onPreview}}
									<SubmitButtonComponent
										@text="Preview"
										@extraClass="previewbutton"
										@onTrigger={{this.openPreview}}
										@noDelay={{true}}
										@theme="blue-outlined"
									/>
								{{/if}}
							</div>
							<div class="actions__bottom">
								<SubmitButtonComponent
									@text={{@mainActionText}}
									@onTrigger={{this.mainAction}}
									@noDelay={{true}}
								/>
							</div>
						</div>
					</div>

				</div>

				<div class="top">
					<div class="top__left">
					</div>
					<div class="top__right">
					</div>
				</div>

				<div class="info">

					<div class="anti-stretch">
						<EditableModelPropertyComponent
							@model={{@model}}
							@canEdit={{false}}
							@editableProperty="name"
						/>
					</div>
				</div>

				<div class="bottom">
					<UsedSourcesListComponent @sources={{this.sourcesUsed}} />
				</div>

			{{else}}
				<div class="info" {{visibleCheckItem this.visibleChanged false}}>
					<div class="anti-stretch">&nbsp;</div>
				</div>
			{{/if}}

		</li>
	</template>
}
